import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Text,
  useIsTouchDevice,
} from "@salestrak/ui";

// ---------------------------------------------------------------------------
// The card grammar's clip rule, as a reusable pair: anything clipped reads
// out in full on hover. Used by the kanban card's custom-field tokens and
// the address property chip; adopt it anywhere a one-line value truncates.
// ---------------------------------------------------------------------------

/** Detects whether the ref'd box clips any descendant's content on EITHER
 *  axis — a one-line `truncate` clips horizontally, a `line-clamp` clips
 *  vertically (+1 absorbs subpixel rounding). An ellipsized element still
 *  reports its full content width in `scrollWidth`, so the readout survives
 *  the ellipsis. Re-measures on container resize and text change. */
export function useIsClipped(text: string) {
  const boxRef = useRef<HTMLElement | null>(null);
  const [clipped, setClipped] = useState(false);

  const recompute = useCallback(() => {
    const box = boxRef.current;
    if (!box) return;
    let next = false;
    for (const el of Array.from(box.querySelectorAll("*")) as HTMLElement[]) {
      if (
        el.scrollWidth > el.clientWidth + 1 ||
        el.scrollHeight > el.clientHeight + 1
      ) {
        next = true;
        break;
      }
    }
    setClipped(next);
  }, []);

  useLayoutEffect(() => {
    recompute();
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(recompute);
    ro.observe(box);
    return () => ro.disconnect();
    // text: re-measure when the content changes.
  }, [recompute, text]);

  const setRef = useCallback((node: HTMLElement | null) => {
    boxRef.current = node;
  }, []);

  return { setRef, clipped };
}

/**
 * One-line 14px text that knows when it's clipped — only a CLIPPED value
 * earns the full-content readout (top side; a property/field NAME tooltip
 * keeps the left edge, so the two never fight over one slot).
 *
 * The readout is a HOVER POPOVER, not a tooltip: Base UI keeps one tooltip
 * open at a time, so a tooltip readout would evict the property's name
 * tooltip when it opened. On the popover layer the two stage instead — the
 * name tooltip fires fast and stays, and after `delay` the full text appears
 * alongside it. `openOnHover` is gated on `clipped`, keeping the measured
 * node mounted under the observer.
 *
 * A coarse pointer has no hover to stage on, so there the text carries the
 * library's `overflowTooltip` instead: a held press reads the clipped value,
 * and because the clipped text is the innermost enabled tooltip trigger it
 * wins that press over the enclosing property-name tooltip — while an
 * unclipped value leaves the name to read.
 */
export function ClippedReadoutText({
  text,
  variant,
  weight,
  delay = 600,
}: {
  text: string;
  variant?: React.ComponentProps<typeof Text>["variant"];
  weight?: React.ComponentProps<typeof Text>["weight"];
  /** Pointer rest before the readout appears (the name tooltip fires first,
   *  so the sequence always reads name → full value). */
  delay?: number;
}) {
  const isTouch = useIsTouchDevice();
  const { setRef, clipped } = useIsClipped(text);

  const content = (
    <Box
      ref={setRef}
      direction="column"
      fill="flex"
      shrink
      minWidth={0}
      overflow="hidden"
    >
      <Text
        size="sm"
        variant={variant}
        weight={weight}
        truncate
        overflowTooltip={isTouch}
      >
        {text}
      </Text>
    </Box>
  );

  if (isTouch) return content;

  return (
    <Popover>
      <PopoverTrigger openOnHover={clipped} delay={delay} render={content} />
      <PopoverPopup side="top" padding="sm">
        <Box maxWidth={280}>
          <Text size="xs">{text}</Text>
        </Box>
      </PopoverPopup>
    </Popover>
  );
}
